import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { get, put, API_URL } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

// const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const UserManagement = ({ setActiveTab }) => {
  const { hasRole, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('PENDING'); // Default to pending
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest'); // Default to newest first
  const [sites, setSites] = useState([]);
  const [selectedSiteUuid, setSelectedSiteUuid] = useState('');
  const navigate = useNavigate();

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      // For Unix timestamp in milliseconds (directly passed as a number)
      let date;
      
      if (typeof dateValue === 'number') {
        // Direct timestamp value
        date = new Date(dateValue);
      } else {
        // String representation - could be ISO date or timestamp string
        const timestamp = Date.parse(dateValue);
        if (isNaN(timestamp)) {
          // console.warn('Invalid date value:', dateValue);
          return 'Invalid date';
        }
        date = new Date(timestamp);
      }
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        // console.warn('Invalid date object created from:', dateValue);
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      // console.error('Error formatting date:', error, 'Value:', dateValue);
      return 'Invalid date';
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let url = `${API_URL}/auth/users`;
        
        // If not admin but has other staff roles, use the status endpoint
        if (!hasRole('ADMIN') || statusFilter) {
          url = `${API_URL}/auth/users/status/${statusFilter}`;
        }
        
        const response = await get(url);
        
        setUsers(response.data);
        
        // Apply initial sorting
        sortUsers(response.data, sortOrder);
      } catch (err) {
        // console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [hasRole, statusFilter]);

  // Sort users by creation date
  const sortUsers = (userList = users, order = sortOrder) => {
    const sortedUsers = [...userList].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      
      if (order === 'newest') {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });
    
    setFilteredUsers(sortedUsers);
  };

  // Handle sort order change
  const handleSortOrderChange = (order) => {
    setSortOrder(order);
    sortUsers(users, order);
  };

  // Apply status filter
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  // Approve pending user
  const handleApproveUser = async (uuid) => {
    try {
      setActionLoading(true);
      
      // Update user status to APPROVED
      await put(`${API_URL}/auth/users`, {
        uuid,
        status: 'APPROVED'
      });
      
      // Refresh user list
      const response = await get(`${API_URL}/auth/users/status/${statusFilter}`);
      setUsers(response.data);
      sortUsers(response.data);
      setSelectedUser(null);
      
    } catch (err) {
      // console.error('Error approving user:', err);
      setError('Failed to approve user. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Deny pending user
  const handleDenyUser = async (uuid) => {
    try {
      setActionLoading(true);
      
      // Update user status to DENIED
      await put(`${API_URL}/auth/users`, {
        uuid,
        status: 'DENIED'
      });
      
      // Refresh user list
      const response = await get(`${API_URL}/auth/users/status/${statusFilter}`);
      setUsers(response.data);
      sortUsers(response.data);
      setSelectedUser(null);
      
    } catch (err) {
      // console.error('Error denying user:', err);
      setError('Failed to deny user. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle switching to Role Management tab
  const handleManageRoles = () => {
    if (setActiveTab) {
      setActiveTab('roles');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left Panel - User Filters & List */}
      <div className="lg:col-span-2">
        <div className="mb-4">
          <h2 className="text-xl font-bold mb-4">User Management</h2>
          
          {/* Status Filter Buttons */}
          <div className="flex flex-wrap mb-4 gap-2">
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                statusFilter === 'PENDING' 
                  ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => handleStatusFilterChange('PENDING')}
            >
              Pending
            </button>
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                statusFilter === 'APPROVED' 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => handleStatusFilterChange('APPROVED')}
            >
              Approved
            </button>
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                statusFilter === 'DENIED' 
                  ? 'bg-red-100 text-red-800 border border-red-300' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => handleStatusFilterChange('DENIED')}
            >
              Denied
            </button>
            {hasRole('ADMIN') && (
              <button
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  !statusFilter
                    ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onClick={() => handleStatusFilterChange('')}
              >
                All Users
              </button>
            )}
          </div>

          {/* Sort Order Controls */}
          <div className="flex mb-4 gap-2">
            <span className="text-sm text-gray-600 mr-2">Sort by:</span>
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                sortOrder === 'newest' 
                  ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => handleSortOrderChange('newest')}
            >
              Newest
            </button>
            <button
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                sortOrder === 'oldest' 
                  ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => handleSortOrderChange('oldest')}
            >
              Oldest
            </button>
          </div>
          
          {/* User count */}
          <p className="text-sm text-gray-600 mb-2">
            {filteredUsers.length} {statusFilter ? statusFilter.toLowerCase() : ''} 
            {filteredUsers.length === 1 ? ' user' : ' users'} found
          </p>
        </div>
        
        {/* User List */}
        <div className="border rounded-lg overflow-hidden">
          {filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No users found with the selected filter.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 max-h-[70vh] overflow-y-auto">
              {filteredUsers.map((user) => (
                <li 
                  key={user.uuid}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    selectedUser?.uuid === user.uuid ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="px-4 py-3">
                    <div className="flex flex-col">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{user.firstName} {user.lastName}</p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                        <div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${user.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' : 
                              user.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                              'bg-red-100 text-red-800'}`}
                          >
                            {user.status}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1">
                        <p className="text-xs text-gray-500">Created: {formatDate(user.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      
      {/* Right Panel - User Details */}
      <div className="lg:col-span-3">
        {selectedUser ? (
          <div className="border rounded-lg p-6">
            <h2 className="text-xl font-bold mb-6">User Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Personal Information</h3>
                <div className="mt-2 space-y-2">
                  <p><span className="font-medium">Name:</span> {selectedUser.firstName} {selectedUser.lastName}</p>
                  <p><span className="font-medium">Email:</span> {selectedUser.email}</p>
                  <p><span className="font-medium">Phone:</span> {selectedUser.cellphone}</p>
                  <p><span className="font-medium">Username:</span> {selectedUser.username}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Account Information</h3>
                <div className="mt-2 space-y-2">
                  <p><span className="font-medium">Status:</span> {selectedUser.status}</p>
                  <p><span className="font-medium">Site:</span> {selectedUser.siteName || 'Not assigned'}</p>
                  <p><span className="font-medium">Unit ID:</span> {selectedUser.unitId || 'Not assigned'}</p>
                  <p><span className="font-medium">Roles:</span> {selectedUser.roles?.join(', ') || 'None'}</p>
                  <p><span className="font-medium">Created:</span> {formatDate(selectedUser.createdAt)}</p>
                  <p><span className="font-medium">Last Updated:</span> {formatDate(selectedUser.updatedAt)}</p>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Actions</h3>
              <div className="flex flex-wrap gap-3">
                {selectedUser.status === 'PENDING' && (
                  <>
                    <button
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-opacity-50 disabled:opacity-50"
                      onClick={() => handleApproveUser(selectedUser.uuid)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : 'Approve User'}
                    </button>
                    <button
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-opacity-50 disabled:opacity-50"
                      onClick={() => handleDenyUser(selectedUser.uuid)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : 'Deny User'}
                    </button>
                  </>
                )}
                
                {hasRole('ADMIN') && selectedUser.status === 'APPROVED' && (
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 disabled:opacity-50"
                    onClick={handleManageRoles}
                    disabled={actionLoading}
                  >
                    Manage Roles
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full p-12 text-center text-gray-500">
            <svg className="h-24 w-24 text-gray-300 mb-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
            <p>Select a user to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement; 